import { MyFatoorah } from './my-fatoorah';
import { t } from '@/locales';
import { callFirebaseFunction } from '@/v2/utils/firebase';
import { separateCountryCode } from '@/v2/utils/phone';
import { Product } from './product';
import { useAuthStore } from '@/v2/store/shared/auth-store';

export const OrderStatus = Object.freeze({
	Draft: 'draft',
	New: 'new',
	Processing: 'processing',
	Shipped: 'shipped',
	Delivered: 'delivered',
	Cancelled: 'cancelled',
	Returned: 'returned',
	Refunded: 'refunded',
});

export const OrderStatusOptions = (props = {}) => {
	const { isNGO = false, order = null } = props || {};

	const pickupFromBranch = Boolean(order?.pickupFromBranch);

	return [
		{ label: t('e-agent.status.draft'), value: OrderStatus.Draft },
		{ label: t('e-agent.status.new'), value: OrderStatus.New },
		{ label: t('e-agent.status.processing'), value: OrderStatus.Processing },
		{ label: t('e-agent.status.shipped'), value: OrderStatus.Shipped },
		{ label: t('e-agent.status.delivered'), value: OrderStatus.Delivered },
		{ label: t('e-agent.status.cancelled'), value: OrderStatus.Cancelled },
		// { label: t('e-agent.status.returned'), value: OrderStatus.Returned },
		// { label: t('e-agent.status.refunded'), value: OrderStatus.Refunded },
	].filter((status) => {
		if (pickupFromBranch && status.value === OrderStatus.Shipped) return false;

		return (
			!isNGO ||
			[OrderStatus.Draft, OrderStatus.New, OrderStatus.Cancelled].includes(status.value)
		);
	});
};

export const PaymentMethod = Object.freeze({
	MyFatoorah: 'my_fatoorah',
	Paylink: 'pay_link',
	BankTransfer: 'bank_transfer',
	CashOnDelivery: 'cash_on_delivery',
	CreditCard: 'credit_card', // TODO: remove this
	MyFatoorahEmbedded: 'my_fatoorah_embedded',
	MyFatoorahRecurring: 'my_fatoorah_recurring',
});

export const PaymentMethodOptions = () => {
   const authStore = useAuthStore();
   const paymentMethods = authStore.firebaseTenantSettings?.paymentMethods;

   const disableCashOnDelivery = paymentMethods?.cash_on_delivery === false;
   const disableBankTransfer = paymentMethods?.bank_transfer === false;

   return [
      {
         label: t('e-agent.orders-report.payment-methods.cash-on-delivery'),
         value: PaymentMethod.CashOnDelivery,
         disabled: disableCashOnDelivery,
      },
      {
         label: t('e-agent.orders-report.payment-methods.bank-transfer'),
         value: PaymentMethod.BankTransfer,
         disabled: disableBankTransfer,
      },
      {
         label: t('e-agent.orders-report.payment-methods.my-fatoorah'),
         value: PaymentMethod.MyFatoorah,
      },
   ].filter(method => !method.disabled);
};

export const OrderPaymentStatus = Object.freeze({
	Pending: 'pending',
	BankTransferUnderReview: 'bank_transfer_under_review',
	Paid: 'paid',
	Failed: 'failed',
	Refunded: 'refunded',
});

export const PaymentStatusOptions = () => [
	{ label: t('e-agent.PaymentStatus.pending'), value: OrderPaymentStatus.Pending },
	{
		label: t('e-agent.PaymentStatus.bank_transfer_under_review'),
		value: OrderPaymentStatus.BankTransferUnderReview,
	},
	{ label: t('e-agent.PaymentStatus.paid'), value: OrderPaymentStatus.Paid },
	{ label: t('e-agent.PaymentStatus.failed'), value: OrderPaymentStatus.Failed },
	{ label: t('e-agent.PaymentStatus.refunded'), value: OrderPaymentStatus.Refunded },
];

export class OrderItem {
	constructor(data = {}) {
		this.productId = data.productId || null;
		this.variantId = data.variantId || null;
		this.name = data.name || '';
		this.quantity = data.quantity || 1;
		this.price = data.price || 0;
		this.sku = data.sku || '';
		this.isVariant = data.isVariant || false;
		this.images = data.images || [];
		this.totalDiscount = data.totalDiscount || 0;
		this.compareAtPrice = data.compareAtPrice || 0;
		this.additionalData = data.additionalData || {};
      this.additionalFields = data.additionalFields || {};
	}

	get featuredImage() {
		if (this.featuredImageUrl) return this.featuredImageUrl;

		return this.images[0] || null;
	}

	static getInventory(item, storeId) {
      if (item.variantId && item.variants?.length) {
         const variant = item.variants.find(v => v.id === item.variantId);
         return variant?.storeInventory?.[storeId]?.quantity || 0;
      }
		return item.storeInventory?.[storeId]?.quantity || 0;
	}

	static fromProduct(p) {
		const product = new Product(p);
		const price = product.getPrice();

		return new OrderItem({
			productId: product.productId || product.id,
			variantId: product.variantId,
			quantity: product.quantity,
			price,
			name: product.name,
			sku: product.sku,
			isVariant: Boolean(product.variantId),
			images: product.images,
			totalDiscount: p.totalDiscount || 0,
			compareAtPrice: product.compareAtPrice || 0,
         additionalFields: product.additionalFields || {},
		});
	}
}

export class Order {
	constructor(data = {}) {
		Object.assign(this, data);
		this.id = data.id || null;
		this.status = data.status || 'draft';
		this.items = (data.items || []).map((item) => new OrderItem(item));
		this.customer = data.customer || {};
		this.shippingAddress = data.shippingAddress || {};
		this.billingAddress = data.billingAddress || {};
		this.paymentMethod = data.paymentMethod || null;
		this.paymentStatus = data.paymentStatus || 'pending';
		this.paymentLink = data.paymentLink || '';
		this.notes = data.notes || '';
		this.isAbandoned = data.isAbandoned || false;
		this.discounts = data.discounts || [];
		this.createdAt = data.createdAt || null;
		this.updatedAt = data.updatedAt || null;
	}

	/**
	 * Generate MyFatoorah payment payload
	 */
	async generatePaymentPayload(tenantId, total) {
		const supplier = await MyFatoorah.getSupplier();

		const { countryCode, localNumber } = separateCountryCode(this.customer.phone);

		return {
			CustomerName: this.getCustomerName(),
			MobileCountryCode: countryCode || null,
			CustomerMobile: localNumber || null,
			CustomerEmail: this.customer.email || null,
			UserDefinedField: String(tenantId || ''),
			NotificationOption: 'LNK',
			InvoiceValue: total,
			DisplayCurrencyIso: 'sar',
			OrderId: this.id,
			Suppliers: [
				{
					SupplierCode: supplier.supplier_code,
					InvoiceShare: total,
				},
			],
			// InvoiceItems: this.items.map(item => ({
			// 	ItemName: item.name || '',
			// 	Quantity: item.quantity || 1,
			// 	UnitPrice: item.price || 0
			// }))
		};
	}

	/**
	 * Get formatted customer name
	 */
	getCustomerName() {
		const firstName = this.customer.firstName || '';
		const lastName = this.customer.lastName || '';
		return `${firstName} ${lastName}`.trim() || 'Customer';
	}

	/**
	 * Check if order is a draft
	 */
	isDraft() {
		return this.status === 'draft';
	}

	/**
	 * Check if order is pending
	 */
	isPending() {
		return this.status === 'pending';
	}

	/**
	 * Check if payment is completed
	 */
	isPaymentCompleted() {
		return this.paymentStatus === 'completed';
	}

	/**
	 * Get payment status variant for UI
	 */
	getPaymentStatusVariant() {
		switch (this.paymentStatus) {
			case 'pending':
				return 'warning';
			case 'processing':
				return 'indigo';
			case 'completed':
				return 'success';
			case 'failed':
			case 'cancelled':
				return 'error';
			case 'refunded':
				return 'gray';
			default:
				return 'gray';
		}
	}

	/**
	 * Clone the order
	 */
	clone() {
		return new Order(JSON.parse(JSON.stringify(this)));
	}

	/**
	 * Convert to plain object
	 */
	toObject() {
		return {
			id: this.id,
			status: this.status,
			items: this.items,
			customer: this.customer,
			shippingAddress: this.shippingAddress,
			billingAddress: this.billingAddress,
			paymentMethod: this.paymentMethod,
			paymentStatus: this.paymentStatus,
			paymentLink: this.paymentLink,
			notes: this.notes,
			isAbandoned: this.isAbandoned,
			discounts: this.discounts,
			createdAt: this.createdAt,
			updatedAt: this.updatedAt,
		};
	}

	static checkout(order) {
		if (
			[PaymentMethod.CreditCard, PaymentMethod.MyFatoorah, PaymentMethod.Paylink].includes(
				order.paymentMethod,
			)
		)
			return;

		if (!order.customerId) {
			console.error('Customer ID is required to checkout');
			return;
		}

		return callFirebaseFunction('checkout', {}, { headers: { userId: order.customerId } });
	}

	static cancelOrder(order) {
		return callFirebaseFunction(
			'cancelOrder',
			{ orderId: order.id },
			{ headers: { userId: order.customerId } },
		);
	}

	static async changeOrderStatus({ orderId, status, customerId }) {
		return callFirebaseFunction(
			'changeOrderStatus',
			{ orderId, status, withValidation: false },
			{ headers: { userId: customerId } },
		);
	}

	static async changePaymentStatus({ orderId, paymentStatus, supplierCode }) {
		return callFirebaseFunction('changeOrderPaymentStatus', {
			orderId,
			paymentStatus,
			supplierCode,
		});
	}
}
